/**
 * The breakpoints a TypeScript file needs, as names instead of numbers.
 *
 * The first four are `styles/breakpoints.scss` copied by hand: Sass cannot export a variable to TS
 * without a build step this project does not have, so the two lists are kept equal by reading them
 * side by side. Change one, change the other in the same commit.
 *
 * The rest are NOT steps of the scale. Each is the width at which one screen's stylesheet already
 * folds, and a `useMediaQuery` next to that stylesheet has to switch at the same pixel, or between the
 * two numbers the drawing and its layout disagree (the geometry of one, the CSS of the other). They
 * are named here so the audit can find them (mobile audit 2026-09-28, sweep20: the same numbers sat in
 * five .tsx files as bare literals). Moving a screen onto the scale means changing its .module.scss
 * and its constant here together; changing only this file breaks that screen between the two widths.
 */

/** `$bp-mobile`: the phone layout, up to and including this width. */
export const BP_MOBILE = 767
/** `$bp-desktop`: the first width that is not a phone. */
export const BP_DESKTOP = 768
/** `$bp-console-stack`: the operator console stacks its columns up to here. */
export const BP_CONSOLE_STACK = 1080
/** `$bp-audience`: the app's audience panel appears from here. */
export const BP_AUDIENCE = 1320

/**
 * The funnel 1 landing (LandingHero, HeroPick, NetworkSection .module.scss): it was laid out before the
 * scale existed and folds at 640. HeroPick's narrow drawing must switch with HeroPick.module.scss:18,
 * which is why this is not simply BP_MOBILE.
 */
export const BP_LANDING_NARROW = 640

/**
 * The onboarding steps (IntroStep, LinkStep, ConfirmStep, PlanCard) and the wizard's own step rows
 * (AppWizard, StepNav, ProductStep): all their .module.scss fold at 660. The steps were tuned for one
 * screen height at that width (the centred block between the lede and the pinned button), so moving
 * them to 767 is a redesign of those screens, not a rename.
 */
export const BP_ONBOARD_COMPACT = 660

/**
 * The funnel 4 landing hero (HeroFrame.module.scss:66) and the sequence board (SequenceView,
 * PackageGate, the former CheckoutDialog .module.scss): at 760 the board no longer reads at its own size, so the
 * hero frame shows the cropped still instead of the video.
 */
export const BP_SAAS_NARROW = 760

/**
 * The funnel 4 "why us" stack (WhyUs.module.scss:654 and its neighbours at `max-width: 960px`): below
 * it a card is taller than the window and a sticky card taller than the window gets stuck instead of
 * sticking.
 */
export const BP_SAAS_STACK = 960
/** The first width WITH the stack: the complement of `max-width: 960px`, so no width is both or neither. */
export const BP_SAAS_STACK_FROM = BP_SAAS_STACK + 1

/**
 * The workspace's sidebar folds into the phone's two bars (Home/nav/fold.scss, `$fold: 860px`). The
 * tour of the workspace (Home/onboarding/PlatformMark) points at the sidebar above it and at the tab
 * bar below it, so it has to switch at the same width as the stylesheets.
 */
export const BP_HOME_FOLD = 860

/** `(max-width: Npx)`, spelled exactly as the .module.scss files spell it. */
export const upTo = (px: number): string => `(max-width: ${px}px)`
/** `(min-width: Npx)`. */
export const atLeast = (px: number): string => `(min-width: ${px}px)`

/** The phone layout as a media query: what `@include bp.mobile` means in a stylesheet. */
export const PHONE_QUERY = upTo(BP_MOBILE)

/** The folded workspace (no sidebar, tab bar at the bottom): what `@include fold.folded` means. */
export const HOME_FOLD_QUERY = upTo(BP_HOME_FOLD)
